import React, { useState, useEffect, FormEvent } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";

// Services & Types
import { ResourcePermissionService } from "services/resource-permission.service";
import UserService from "services/user.service";
import {
  ISaveResourcePermission,
  ResourceType,
  IUser,
} from "models/church.types";

interface IOption {
  value: number;
  label: string;
}

export default function ResourcePermissionRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingResources, setLoadingResources] = useState(false);

  const [users, setUsers] = useState<IOption[]>([]);
  const [resourceOptions, setResourceOptions] = useState<IOption[]>([]);
  const [errors, setErrors] = useState<{
    userId?: string;
    resourceId?: string;
  }>({});

  // Initialisation de la permission
  const [permission, setPermission] = useState<ISaveResourcePermission>({
    resourceType: ResourceType.FUND,
    resourceId: 0,
    canRead: true,
    canWrite: false,
    canDelete: false,
    canApprove: false,
    userId: 0,
  });

  // Styles personnalisés React-Select (Cohérence visuelle)
  const customSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "#0F172A",
      borderColor: state.isFocused ? "#D4AF37" : "#334155",
      borderRadius: "0.5rem",
      padding: "3px",
      minHeight: "45px",
      color: "white",
      boxShadow: "none",
      transition: "all 0.2s",
      "&:hover": { borderColor: "#D4AF37" },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1E293B",
      border: "1px solid #334155",
      zIndex: 100,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? "#D4AF37" : "transparent",
      color: state.isFocused ? "#0F172A" : "white",
      padding: "10px",
      cursor: "pointer",
      fontSize: "0.875rem",
      "&:active": { backgroundColor: "#D4AF37" },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "white",
      fontSize: "0.875rem",
    }),
    input: (base: any) => ({ ...base, color: "white" }),
    placeholder: (base: any) => ({
      ...base,
      color: "#64748b",
      fontSize: "0.875rem",
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "#475569",
      "&:hover": { color: "#D4AF37" },
    }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  // 1. Initialisation : Liste des utilisateurs + Données de modification
  useEffect(() => {
    const initUsersAndPermission = async () => {
      try {
        const userData = await UserService.getAll(0, 500);
        const formattedUsers = (userData.data || []).map((u: IUser) => ({
          value: u.id,
          label: `${u.firstName || ""} ${u.lastName || ""} (${u.login})`,
        }));
        setUsers(formattedUsers);

        if (isEditMode && id) {
          const data = await ResourcePermissionService.getById(parseInt(id));
          setPermission({
            resourceType: data.resourceType,
            resourceId: data.resourceId,
            canRead: data.canRead,
            canWrite: data.canWrite,
            canDelete: data.canDelete,
            canApprove: data.canApprove,
            userId: data.userId,
          });
        }
      } catch (error) {
        console.error("Erreur d'initialisation:", error);
        toast.error("Erreur lors du chargement initial");
      }
    };
    initUsersAndPermission();
  }, [id, isEditMode]);

  // 2. Chargement dynamique des ressources cibles via l'endpoint Lookup générique
  useEffect(() => {
    const loadResources = async () => {
      setLoadingResources(true);
      try {
        // APPEL UNIQUE GÉNÉRIQUE AU BACKEND
        const data = await ResourcePermissionService.getLookupResources(
          permission.resourceType,
        );

        const options = data.map((item) => ({
          value: item.id,
          label: `${item.displayName}`,
        }));

        setResourceOptions(options);

        // Si on change le type en création, on reset l'ID sélectionné
        if (!isEditMode) {
          setPermission((prev) => ({ ...prev, resourceId: 0 }));
        }
      } catch (error) {
        console.error("Erreur lors du chargement des ressources:", error);
        toast.error("Impossible de charger les entités associées");
      } finally {
        setLoadingResources(false);
      }
    };

    loadResources();
  }, [permission.resourceType, isEditMode]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validation
    const newErrors: { userId?: string; resourceId?: string } = {};
    if (!permission.userId || permission.userId === 0) {
      newErrors.userId = "Veuillez sélectionner un utilisateur";
    }
    if (!permission.resourceId || permission.resourceId === 0) {
      newErrors.resourceId = "Veuillez sélectionner une ressource cible";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        await ResourcePermissionService.update({
          ...permission,
          id: parseInt(id),
        });
        toast.success("Droits d'accès mis à jour");
      } else {
        await ResourcePermissionService.create(permission);
        toast.success("Droits d'accès enregistrés");
      }
      history.push("/admin/resource-permissions");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement des droits d'accès.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-start md:justify-center mt-14 p-4 w-full min-h-screen py-12 md:py-6">
      <div className="w-full max-w-2xl">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-xl bg-[#1E293B] border border-slate-700 shadow-2xl">
          <div className="rounded-t-xl bg-[#0F172A] px-6 py-4 border-b border-slate-700">
            <div className="flex justify-between items-center">
              <h6 className="text-white text-sm font-bold uppercase tracking-widest">
                <i className="fas fa-user-lock mr-2 text-[#D4AF37]"></i>
                {isEditMode
                  ? "Modifier l'autorisation"
                  : "Attribuer une autorisation"}
              </h6>
              <button
                type="button"
                onClick={() => history.goBack()}
                className="text-slate-400 hover:text-white text-xs font-bold uppercase transition-all"
              >
                <i className="fas fa-times mr-1"></i> Fermer
              </button>
            </div>
          </div>

          <div className="flex-auto px-6 py-8">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-6">
                {/* Utilisateur */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                    <i className="fas fa-user mr-2"></i>Utilisateur cible *
                  </label>
                  <Select
                    options={users}
                    styles={customSelectStyles}
                    isClearable={!isEditMode}
                    isDisabled={isEditMode}
                    placeholder="Sélectionner ou chercher un utilisateur..."
                    value={
                      users.find((u) => u.value === permission.userId) || null
                    }
                    onChange={(selected) => {
                      setPermission({
                        ...permission,
                        userId: selected ? selected.value : 0,
                      });
                      if (errors.userId)
                        setErrors({ ...errors, userId: undefined });
                    }}
                    noOptionsMessage={() => "Aucun utilisateur trouvé"}
                  />
                  {errors.userId && (
                    <p className="text-red-500 text-[10px] mt-1 italic">
                      {errors.userId}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Type de Ressource */}
                  <div>
                    <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                      <i className="fas fa-folder mr-2"></i>Type de ressource *
                    </label>
                    <select
                      value={permission.resourceType}
                      onChange={(e) =>
                        setPermission({
                          ...permission,
                          resourceType: e.target.value as ResourceType,
                        })
                      }
                      className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all h-[45px]"
                    >
                      <option value={ResourceType.FUND}>Caisse</option>
                      <option value={ResourceType.TRAINING}>Formation</option>
                      <option value={ResourceType.EVENT}>Événement</option>
                    </select>
                  </div>

                  {/* ID de la Ressource - DYNAMIQUE VIA SELECT ET ENDPOINT LOOKUP */}
                  <div>
                    <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                      <i className="fas fa-fingerprint mr-2"></i>
                      {loadingResources
                        ? "Chargement des options..."
                        : "Ressource ciblée *"}
                    </label>
                    <Select
                      options={resourceOptions}
                      styles={customSelectStyles}
                      isClearable
                      isLoading={loadingResources}
                      placeholder={
                        loadingResources
                          ? "Chargement..."
                          : "Choisir l'élément cible..."
                      }
                      value={
                        resourceOptions.find(
                          (r) => r.value === permission.resourceId,
                        ) || null
                      }
                      onChange={(selected) => {
                        setPermission({
                          ...permission,
                          resourceId: selected ? selected.value : 0,
                        });
                        if (errors.resourceId)
                          setErrors({ ...errors, resourceId: undefined });
                      }}
                      noOptionsMessage={() => "Aucun élément disponible"}
                    />
                    {errors.resourceId && (
                      <p className="text-red-500 text-[10px] mt-1 italic">
                        {errors.resourceId}
                      </p>
                    )}
                  </div>
                </div>

                {/* Matrice des droits d'accès (Toggles) */}
                <div className="border-t border-slate-700/60 pt-6">
                  <span className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-4 block">
                    <i className="fas fa-key mr-2"></i>Privilèges accordés
                  </span>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Droit de Lecture */}
                    <div className="bg-[#0F172A] p-4 rounded-lg border border-slate-700/50 flex items-center justify-between">
                      <div>
                        <span className="text-xs text-white font-bold block">
                          Lecture (Read)
                        </span>
                        <span className="text-[10px] text-slate-400">
                          Autorise la visualisation de la ressource
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setPermission({
                            ...permission,
                            canRead: !permission.canRead,
                          })
                        }
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                          permission.canRead ? "bg-emerald-500" : "bg-slate-700"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-[#0F172A] transition-transform ${
                            permission.canRead
                              ? "translate-x-6"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>

                    {/* Droit d'Écriture */}
                    <div className="bg-[#0F172A] p-4 rounded-lg border border-slate-700/50 flex items-center justify-between">
                      <div>
                        <span className="text-xs text-white font-bold block">
                          Écriture (Write)
                        </span>
                        <span className="text-[10px] text-slate-400">
                          Autorise la modification et mise à jour
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setPermission({
                            ...permission,
                            canWrite: !permission.canWrite,
                          })
                        }
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                          permission.canWrite
                            ? "bg-emerald-500"
                            : "bg-slate-700"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-[#0F172A] transition-transform ${
                            permission.canWrite
                              ? "translate-x-6"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>

                    {/* Droit de Suppression */}
                    <div className="bg-[#0F172A] p-4 rounded-lg border border-slate-700/50 flex items-center justify-between">
                      <div>
                        <span className="text-xs text-white font-bold block">
                          Suppression (Delete)
                        </span>
                        <span className="text-[10px] text-slate-400">
                          Autorise le retrait de l'élément
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setPermission({
                            ...permission,
                            canDelete: !permission.canDelete,
                          })
                        }
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                          permission.canDelete ? "bg-rose-500" : "bg-slate-700"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-[#0F172A] transition-transform ${
                            permission.canDelete
                              ? "translate-x-6"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>

                    {/* Droit d'Approbation */}
                    <div className="bg-[#0F172A] p-4 rounded-lg border border-slate-700/50 flex items-center justify-between">
                      <div>
                        <span className="text-xs text-white font-bold block">
                          Validation (Approve)
                        </span>
                        <span className="text-[10px] text-slate-400">
                          Autorise la validation budgétaire / administrative
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setPermission({
                            ...permission,
                            canApprove: !permission.canApprove,
                          })
                        }
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                          permission.canApprove
                            ? "bg-indigo-500"
                            : "bg-slate-700"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-[#0F172A] transition-transform ${
                            permission.canApprove
                              ? "translate-x-6"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-10 flex justify-end gap-4 border-t border-slate-700 pt-6">
                <button
                  type="button"
                  onClick={() => history.push("/admin/resource-permissions")}
                  className="px-6 py-3 rounded-lg font-bold text-xs uppercase text-slate-400 hover:text-white transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#D4AF37] text-[#0F172A] px-10 py-3 rounded-lg font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-lg disabled:opacity-50"
                >
                  {loading ? (
                    <i className="fas fa-spinner animate-spin mr-2"></i>
                  ) : (
                    <i className="fas fa-save mr-2"></i>
                  )}
                  {isEditMode ? "Mettre à jour" : "Enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
